import React from 'react';
import {
  Heart, CheckCircle, ChevronRight, Loader2,
  Mail, User, Gift, Sparkles, Briefcase
} from 'lucide-react';

const DonationModal = ({
  primaryColor,
  business,
  selectedCampaign,
  donationAmount,
  setDonationAmount,
  customAmount,
  setCustomAmount,
  donorEmail,
  setDonorEmail,
  donorName,
  setDonorName,
  isEmployee,
  setIsEmployee,
  employeeVerified,
  setEmployeeVerified,
  employeeUser,
  matchInfo,
  verifying,
  processing,
  donationFlowStep,
  setDonationFlowStep,
  handleDonate,
  verifyEmployee,
  setShowDonateModal,
  setShowLoginModal,
}) => {
  const onClose = () => {
    setShowDonateModal(false);
    setDonationFlowStep('choice');
    if (!employeeUser) {
      setEmployeeVerified(false);
      setIsEmployee(false);
      setDonorEmail('');
      setDonorName('');
    }
  };

  const effectiveAmount = customAmount ? parseFloat(customAmount) : donationAmount;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-bold text-gray-900" data-testid="donation-modal-title">
              {(!employeeVerified && !employeeUser) ? 'Start Your Donation' : 'Complete Your Donation'}
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl" data-testid="donation-modal-close">&times;</button>
          </div>
        </div>

        <div className="p-6">
          {employeeUser ? (
            <LoggedInEmployeeForm
              primaryColor={primaryColor}
              business={business}
              employeeUser={employeeUser}
              matchInfo={matchInfo}
              selectedCampaign={selectedCampaign}
              donationAmount={donationAmount}
              setDonationAmount={setDonationAmount}
              customAmount={customAmount}
              setCustomAmount={setCustomAmount}
              effectiveAmount={effectiveAmount}
              processing={processing}
              handleDonate={handleDonate}
            />
          ) : donationFlowStep === 'choice' && business.guest_donations_enabled ? (
            <DonationChoiceStep
              primaryColor={primaryColor}
              business={business}
              setDonationFlowStep={setDonationFlowStep}
              setIsEmployee={setIsEmployee}
              setEmployeeVerified={setEmployeeVerified}
            />
          ) : (donationFlowStep === 'employee' || !business.guest_donations_enabled) && !employeeVerified ? (
            <EmployeeVerifyStep
              primaryColor={primaryColor}
              business={business}
              donorEmail={donorEmail}
              setDonorEmail={setDonorEmail}
              verifyEmployee={verifyEmployee}
              verifying={verifying}
              setDonationFlowStep={setDonationFlowStep}
            />
          ) : (
            <GuestOrVerifiedForm
              primaryColor={primaryColor}
              business={business}
              isEmployee={isEmployee}
              matchInfo={matchInfo}
              employeeUser={employeeUser}
              selectedCampaign={selectedCampaign}
              donorName={donorName}
              setDonorName={setDonorName}
              donorEmail={donorEmail}
              setDonorEmail={setDonorEmail}
              donationAmount={donationAmount}
              setDonationAmount={setDonationAmount}
              customAmount={customAmount}
              setCustomAmount={setCustomAmount}
              effectiveAmount={effectiveAmount}
              processing={processing}
              handleDonate={handleDonate}
              setShowLoginModal={setShowLoginModal}
            />
          )}
        </div>
      </div>
    </div>
  );
};

/* ===== Sub-components ===== */

const AmountSelector = ({ donationAmount, setDonationAmount, customAmount, setCustomAmount, primaryColor }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-3">Select Amount</label>
    <div className="grid grid-cols-4 gap-2 mb-3">
      {[25, 50, 100, 250].map((amt) => (
        <button
          key={amt}
          onClick={() => { setDonationAmount(amt); setCustomAmount(''); }}
          className={`py-3 rounded-xl font-semibold transition-all ${donationAmount === amt && !customAmount ? 'text-white shadow-lg' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          style={donationAmount === amt && !customAmount ? { background: primaryColor } : {}}
          data-testid={`amount-${amt}`}
        >
          ${amt}
        </button>
      ))}
    </div>
    <div className="relative">
      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-semibold">$</span>
      <input
        type="number"
        value={customAmount}
        onChange={(e) => { setCustomAmount(e.target.value); if (e.target.value) setDonationAmount(0); }}
        placeholder="Custom amount"
        className="w-full pl-8 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500"
        min="1"
        data-testid="custom-amount-input"
      />
    </div>
  </div>
);

const DonateButton = ({ handleDonate, processing, effectiveAmount, primaryColor, icon = true }) => (
  <button
    onClick={handleDonate}
    disabled={processing || !effectiveAmount}
    className="w-full py-4 text-white rounded-xl font-semibold text-lg transition-all hover:shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
    style={{ background: primaryColor }}
    data-testid="donate-submit-btn"
  >
    {processing ? (
      <><Loader2 className="w-5 h-5 animate-spin" /> Processing...</>
    ) : (
      <>
        {icon && <Heart className="w-5 h-5" />}
        Donate ${effectiveAmount}
      </>
    )}
  </button>
);

/* Step: Logged-in employee — skip to payment */
const LoggedInEmployeeForm = ({ primaryColor, business, employeeUser, matchInfo, selectedCampaign, donationAmount, setDonationAmount, customAmount, setCustomAmount, effectiveAmount, processing, handleDonate }) => (
  <div className="space-y-6">
    <div className="bg-green-50 border border-green-200 rounded-xl p-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
          <CheckCircle className="w-5 h-5 text-green-600" />
        </div>
        <div>
          <p className="font-semibold text-green-800">Welcome back, {employeeUser.name || employeeUser.email}!</p>
          <p className="text-sm text-green-600">
            {matchInfo?.match_enabled
              ? `Your donation will be matched ${matchInfo.match_ratio}:1 by ${business.name}`
              : 'Employer matching may apply to eligible campaigns.'}
          </p>
        </div>
      </div>
    </div>

    {selectedCampaign && (
      <div className="bg-gray-50 rounded-xl p-4">
        <p className="text-xs text-gray-500 mb-1">Donating to</p>
        <p className="font-semibold text-gray-900">{selectedCampaign.title}</p>
        <p className="text-sm text-gray-500">{selectedCampaign.charity_name}</p>
      </div>
    )}

    <AmountSelector donationAmount={donationAmount} setDonationAmount={setDonationAmount} customAmount={customAmount} setCustomAmount={setCustomAmount} primaryColor={primaryColor} />
    <DonateButton handleDonate={handleDonate} processing={processing} effectiveAmount={effectiveAmount} primaryColor={primaryColor} icon={false} />
  </div>
);

/* Step 0: Guest vs Employee choice */
const DonationChoiceStep = ({ primaryColor, business, setDonationFlowStep, setIsEmployee, setEmployeeVerified }) => (
  <div className="space-y-6">
    <div className="text-center mb-6">
      <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: `${primaryColor}15` }}>
        <Heart className="w-8 h-8" style={{ color: primaryColor }} />
      </div>
      <h4 className="font-semibold text-gray-900">How would you like to donate?</h4>
      <p className="text-sm text-gray-500 mt-1">Choose how you&apos;d like to proceed with your donation</p>
    </div>

    <button
      onClick={() => { setDonationFlowStep('guest'); setIsEmployee(false); setEmployeeVerified(true); }}
      className="w-full p-4 border-2 border-gray-200 rounded-xl hover:border-blue-300 hover:bg-blue-50 transition-all text-left"
      data-testid="donate-as-guest-btn"
    >
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
          <User className="w-6 h-6 text-blue-600" />
        </div>
        <div>
          <p className="font-semibold text-gray-900">Donate as Guest</p>
          <p className="text-sm text-gray-500">Quick donation without an account</p>
        </div>
        <ChevronRight className="w-5 h-5 text-gray-400 ml-auto" />
      </div>
    </button>

    <button
      onClick={() => setDonationFlowStep('employee')}
      className="w-full p-4 border-2 border-gray-200 rounded-xl hover:border-green-300 hover:bg-green-50 transition-all text-left"
      data-testid="donate-as-employee-btn"
    >
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
          <Briefcase className="w-6 h-6 text-green-600" />
        </div>
        <div>
          <p className="font-semibold text-gray-900">I&apos;m an Employee of {business.name}</p>
          <p className="text-sm text-gray-500">Sign in for employer matching benefits</p>
        </div>
        <ChevronRight className="w-5 h-5 text-gray-400 ml-auto" />
      </div>
    </button>
  </div>
);

/* Step 1: Employee email verification */
const EmployeeVerifyStep = ({ primaryColor, business, donorEmail, setDonorEmail, verifyEmployee, verifying, setDonationFlowStep }) => (
  <div className="space-y-6">
    <div className="text-center mb-6">
      <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: `${primaryColor}15` }}>
        <Mail className="w-8 h-8" style={{ color: primaryColor }} />
      </div>
      <h4 className="font-semibold text-gray-900">Employee Sign In</h4>
      <p className="text-sm text-gray-500 mt-1">Enter your work email to verify your employment</p>
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Work Email</label>
      <input
        type="email"
        value={donorEmail}
        onChange={(e) => setDonorEmail(e.target.value)}
        placeholder="you@company.com"
        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        data-testid="employee-email-input"
      />
    </div>

    <button
      onClick={verifyEmployee}
      disabled={!donorEmail || verifying}
      className="w-full py-3 text-white rounded-xl font-semibold transition-all hover:shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
      style={{ background: primaryColor }}
      data-testid="verify-employee-btn"
    >
      {verifying ? (
        <><Loader2 className="w-4 h-4 animate-spin" /> Verifying...</>
      ) : (
        <>Verify Employee Status <ChevronRight className="w-4 h-4" /></>
      )}
    </button>

    {business.guest_donations_enabled && (
      <button onClick={() => setDonationFlowStep('choice')} className="w-full py-2 text-gray-600 hover:text-gray-800 text-sm">
        &larr; Back to options
      </button>
    )}

    {!business.guest_donations_enabled && (
      <p className="text-xs text-amber-600 text-center">Note: Only verified employees can donate to this campaign.</p>
    )}
  </div>
);

/* Step 2: Guest or verified employee payment form */
const GuestOrVerifiedForm = ({ primaryColor, business, isEmployee, matchInfo, employeeUser, selectedCampaign, donorName, setDonorName, donorEmail, setDonorEmail, donationAmount, setDonationAmount, customAmount, setCustomAmount, effectiveAmount, processing, handleDonate, setShowLoginModal }) => (
  <div className="space-y-6">
    {/* Status Banner */}
    {isEmployee ? (
      <div className="bg-green-50 border border-green-200 rounded-xl p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
            <CheckCircle className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <p className="font-semibold text-green-800">Employee Verified!</p>
            <p className="text-sm text-green-600">
              {matchInfo?.match_enabled
                ? `Your donation will be matched ${matchInfo.match_ratio}:1 by ${business.name}`
                : 'Employer matching may apply to eligible campaigns.'}
            </p>
          </div>
        </div>
        {!employeeUser && (
          <button
            onClick={() => setShowLoginModal(true)}
            className="mt-3 w-full py-2 border border-green-300 text-green-700 rounded-lg text-sm font-medium hover:bg-green-100 transition-all"
          >
            Sign in to track your giving history
          </button>
        )}
      </div>
    ) : (
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
            <User className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <p className="font-semibold text-blue-800">Guest Donation</p>
            <p className="text-sm text-blue-600">Thank you for your generosity!</p>
          </div>
        </div>
      </div>
    )}

    {/* Selected Campaign */}
    {selectedCampaign && (
      <div className="bg-gray-50 rounded-xl p-4">
        <p className="text-xs text-gray-500 mb-1">Donating to</p>
        <p className="font-semibold text-gray-900">{selectedCampaign.title}</p>
        <p className="text-sm text-gray-500">{selectedCampaign.charity_name}</p>
        {selectedCampaign.employer_match_enabled && isEmployee && (
          <div className="mt-2 flex items-center gap-1 text-green-600 text-xs">
            <Gift className="w-3 h-3" />
            <span>{selectedCampaign.employer_match_ratio || 1}:1 matching active</span>
          </div>
        )}
      </div>
    )}

    {/* Name Input */}
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Your Name (optional)</label>
      <input
        type="text"
        value={donorName}
        onChange={(e) => setDonorName(e.target.value)}
        placeholder="Anonymous"
        className="w-full px-4 py-3 border border-gray-200 rounded-xl"
        data-testid="donor-name-input"
      />
    </div>

    {/* Email Input */}
    {!isEmployee && (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Your Email <span className="text-red-500">*</span></label>
        <input
          type="email"
          value={donorEmail}
          onChange={(e) => setDonorEmail(e.target.value)}
          placeholder="you@email.com"
          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500"
          required
          data-testid="donor-email-input"
        />
        <p className="text-xs text-gray-400 mt-1">Required for your tax receipt</p>
      </div>
    )}

    <AmountSelector donationAmount={donationAmount} setDonationAmount={setDonationAmount} customAmount={customAmount} setCustomAmount={setCustomAmount} primaryColor={primaryColor} />

    {/* Impact Preview for matched employee donations */}
    {selectedCampaign?.employer_match_enabled && isEmployee && (
      <div className="bg-purple-50 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-4 h-4 text-purple-600" />
          <span className="font-semibold text-purple-800">Your Total Impact</span>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-purple-600">Your donation</p>
            <p className="text-lg font-bold text-purple-700">${effectiveAmount.toLocaleString()}</p>
          </div>
          <div className="text-2xl text-purple-400">+</div>
          <div>
            <p className="text-sm text-purple-600">Employer match</p>
            <p className="text-lg font-bold text-purple-700">${(effectiveAmount * (selectedCampaign.employer_match_ratio || 1)).toLocaleString()}</p>
          </div>
          <div className="text-2xl text-purple-400">=</div>
          <div>
            <p className="text-sm text-purple-600">Total</p>
            <p className="text-xl font-bold text-purple-800">${(effectiveAmount * (1 + (selectedCampaign.employer_match_ratio || 1))).toLocaleString()}</p>
          </div>
        </div>
      </div>
    )}

    <DonateButton handleDonate={handleDonate} processing={processing} effectiveAmount={effectiveAmount} primaryColor={primaryColor} />

    <p className="text-xs text-center text-gray-400">Secure payment powered by Every.org. Tax receipt included.</p>
  </div>
);

export default DonationModal;
